import cn from 'clsx';
import { FC, ReactNode } from 'react';

import { ColorPalette, IconType } from '../../types';
import { colorToTailwindTheme } from '../../utils';
import Icon from '../Icon';

import s from './ControlList.module.scss';

type CustomIcon = {
  type: IconType;
  size: number;
};

export interface ControlListProps {
  title: ReactNode;
  subText?: string;
  amount?: string;
  amountCaption?: string;
  titleColor?: ColorPalette;
  subTextColor?: ColorPalette;
  amountColor?: ColorPalette;
  amountCaptionColor?: ColorPalette;
  image?: ReactNode;
  right?: ReactNode;
  className?: string;
  disabled?: boolean;
  onClick?: () => void;
  checked?: boolean;
  customIcon?: CustomIcon;
}

const ControlList: FC<React.PropsWithChildren<React.PropsWithChildren<ControlListProps>>> = ({
  title,
  titleColor,
  subText,
  subTextColor,
  image,
  right,
  amount,
  amountCaption,
  amountColor,
  amountCaptionColor,
  className,
  onClick,
  disabled = false,
  checked = false,
  customIcon
}) => {
  const tailwindTitleColor = colorToTailwindTheme(titleColor || 'gray.800', 'text');
  const tailwindSubTextColor = colorToTailwindTheme(subTextColor || 'gray.700', 'text');
  const tailwindAmountColor = colorToTailwindTheme(amountColor || 'gray.800', 'text');
  const tailwindAmountCaptionColor = colorToTailwindTheme(amountCaptionColor || 'gray.600', 'text');

  const handleClickList = () => {
    if (disabled) {
      return;
    }

    onClick?.();
  };

  return (
    <div
      onClick={handleClickList}
      className={cn(
        s.wrap,
        {
          [s.disabled as string]: disabled
        },
        className
      )}
    >
      <div className={cn(s.left)}>
        <div className={s.left_icon_wrapper}>
          {customIcon ? (
            <Icon className={s.left_icon} color={checked ? 'violet.600' : 'gray.300'} type={customIcon.type} size={customIcon.size} />
          ) : (
            <Icon className={s.left_icon} color={checked ? 'violet.600' : 'gray.300'} type={checked ? 'check-solid' : 'check-line'} size={28} />
          )}
        </div>

        <div className={s.left_inner}>
          {image && <div className={s.image}>{image}</div>}
          <div>
            <p className={cn(s.title, tailwindTitleColor)}>{title}</p>
            {subText && (
              <p
                className={cn(s.subText, tailwindSubTextColor, {
                  [s.mt as string]: amountCaption
                })}
              >
                {subText}
              </p>
            )}
          </div>
        </div>
      </div>

      {amount && (
        <div className={s.right_amount}>
          <p className={cn(s.amount, tailwindAmountColor)}>{amount}</p>
          <p className={cn(s.amountCaption, tailwindAmountCaptionColor)}>{amountCaption}</p>
        </div>
      )}
      {right && <div className={s.right}>{right}</div>}
    </div>
  );
};

export default ControlList;
